import { useState } from 'react';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qmaosnlsngrm4lm200';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setStatusMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setStatusMessage('留言内容不能超过500个字符');
      return;
    }

    setStatus('loading');

    try {
      const payload = new FormData(form);
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setStatus('error');
          setStatusMessage('提交失败，请稍后再试。');
        } else {
          setStatus('error');
          setStatusMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请检查您的连接后重试。');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          您的姓名 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-name"
          type="text"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900"
          placeholder="请输入您的姓名"
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          电子邮箱 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900"
          placeholder="请输入您的电子邮箱"
        />
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          required
          value={formData.subject}
          onChange={handleChange}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900"
          placeholder="请输入留言主题"
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors text-foreground-900 resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
      </div>

      <div className="contact-form-alt-field">
        <input
          type="text"
          name="website_alt"
          value={formData.website_alt}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status !== 'idle' && (
        <div
          className={`text-sm p-3 rounded-md ${
            status === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : status === 'error'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-secondary-50 text-secondary-700 border border-secondary-200'
          }`}
        >
          {statusMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
      >
        {status === 'loading' ? '发送中...' : '发送留言'}
      </button>
    </form>
  );
}